import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { NODE_TO_BLOCK_TYPE } from "../document-serializer";

export const blockTypeAttrKey = new PluginKey("blockTypeAttr");

/**
 * `data-block-type` on every top-level node of the Flow canvas — the block
 * type it saves as ("paragraph", "equation", "table", …), the same name the
 * API and the Block editor use. The canvas's nodes otherwise say what they are
 * in a dozen ways (a <p>, an <h2>, a node view's own data-type), so a test
 * that asks "is every block of the document on the canvas, in order, as the
 * right kind of block" had nothing to read.
 *
 * A decoration rather than a node attribute: it is derived, never parsed
 * back, and so cannot leak into the saved content or the clipboard.
 */
export const BlockTypeAttr = Extension.create({
  name: "blockTypeAttr",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: blockTypeAttrKey,
        state: {
          init: (_, { doc }) => build(doc),
          apply: (tr, prev) => (tr.docChanged ? build(tr.doc) : prev),
        },
        props: {
          decorations: (state) => blockTypeAttrKey.getState(state) as DecorationSet,
        },
      }),
    ];
  },
});

type Doc = Parameters<typeof DecorationSet.create>[0];

function build(doc: Doc): DecorationSet {
  const decos: Decoration[] = [];
  doc.forEach((node, offset) => {
    decos.push(
      Decoration.node(offset, offset + node.nodeSize, {
        "data-block-type": NODE_TO_BLOCK_TYPE[node.type.name] ?? node.type.name,
      }),
    );
  });
  return DecorationSet.create(doc, decos);
}
